import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import BookHero from "./components/BookHero";
import BookTOC from "./components/BookTOC";
import CastSection from "./components/CastSection";
import BookSectionComp from "./components/BookSection";
import SEOHead from "@/components/feature/SEOHead";
import { bookSections } from "@/mocks/bookData";

const ALL_SECTIONS = ["characters", ...bookSections.map((s) => s.id)];

const bookSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${import.meta.env.VITE_SITE_URL || ""}/book#webpage`,
    "url": `${import.meta.env.VITE_SITE_URL || ""}/book`,
    "name": "Read The Wraith | Full Post-Apocalyptic Survival Novel Online",
    "description":
      "Read the complete Wraith novel online — Jordan Reyes fights through the zombie apocalypse to find Aidan in this post-Green Monday survival story of love, loss, and resilience.",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "@id": `${import.meta.env.VITE_SITE_URL || ""}/#website`,
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": "The Wraith",
    "alternateName": "The Wraith Chronicles",
    "description":
      "The complete Wraith novel — Jordan 'The Wraith' Reyes battles through the post-apocalyptic zombie wasteland to find Aidan Lee after Green Monday. A story of survival, redemption, and love.",
    "genre": ["Post-Apocalyptic Fiction", "Zombie Survival", "Romance"],
    "inLanguage": "en",
    "url": `${import.meta.env.VITE_SITE_URL || ""}/book`,
    "character": [
      { "@type": "Person", "name": "Jordan Reyes", "alternateName": "The Wraith" },
      { "@type": "Person", "name": "Aidan Lee", "alternateName": "The Chef" },
      { "@type": "Person", "name": "Nina" },
    ],
    "isAccessibleForFree": true,
    "accessMode": "textual",
  },
];

export default function BookPage() {
  const [activeSection, setActiveSection] = useState("characters");
  const [showFloat, setShowFloat] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloat(window.scrollY > window.innerHeight);

      // Determine active section by scroll position
      for (let i = ALL_SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(ALL_SECTIONS[i]);
        if (el && window.scrollY >= el.offsetTop - 160) {
          setActiveSection(ALL_SECTIONS[i]);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const scrollToTOC = useCallback(() => {
    const el = document.getElementById("toc");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handlePrev = useCallback(
    (currentId: string) => {
      const idx = ALL_SECTIONS.indexOf(currentId);
      if (idx > 0) scrollTo(ALL_SECTIONS[idx - 1]);
    },
    [scrollTo]
  );

  const handleNext = useCallback(
    (currentId: string) => {
      const idx = ALL_SECTIONS.indexOf(currentId);
      if (idx < ALL_SECTIONS.length - 1) scrollTo(ALL_SECTIONS[idx + 1]);
    },
    [scrollTo]
  );

  return (
    <div className="bg-[#0A0A0A] min-h-screen w-full">
      <SEOHead
        title="Read The Wraith | Full Post-Apocalyptic Survival Novel Online"
        description="Read the complete Wraith novel online — Jordan Reyes fights through the zombie apocalypse to find Aidan in this post-Green Monday survival story of love, loss, and resilience."
        keywords="The Wraith novel, read online, Jordan Reyes, Aidan Lee, post-apocalyptic book, zombie survival fiction"
        canonical="/book"
        ogTitle="Read The Wraith — The Complete Survival Novel"
        ogDescription="Read the full story. Jordan Reyes. Aidan Lee. Nina. The farmhouse. The end — and the beginning."
        ogType="book"
        schema={bookSchema}
      />
      <Navbar />

      {/* Hero */}
      <BookHero />

      {/* Table of Contents */}
      <BookTOC activeSection={activeSection} onNavigate={scrollTo} />

      {/* Cast of Characters */}
      <CastSection />

      {/* Chapters */}
      {bookSections.map((section, idx) => (
        <BookSectionComp
          key={section.id}
          section={section}
          isFirst={idx === 0}
          isLast={idx === bookSections.length - 1}
          onPrev={() => handlePrev(section.id)}
          onNext={() => handleNext(section.id)}
          onTOC={scrollToTOC}
        />
      ))}

      {/* End of Book */}
      <div className="w-full px-6 py-20 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-px bg-[#1E1E1E]" />
            <i className="ri-sword-line text-[#C8922A]/40 text-sm" />
            <div className="w-16 h-px bg-[#1E1E1E]" />
          </div>
          <div className="font-['Special_Elite'] text-[#333] text-2xl italic mb-4">
            fin.
          </div>
          <div className="font-['Share_Tech_Mono'] text-[#222] text-xs tracking-[0.35em] uppercase mb-12">
            The Wraith &mdash; {new Date().getFullYear()}
          </div>

          {/* I Want More CTA */}
          <div className="border border-[#1E1E1E] bg-[#0D0D0D] px-8 py-10 max-w-xl mx-auto">
            <p className="font-['Special_Elite'] text-[#555] text-lg italic mb-2">
              Not ready to let go?
            </p>
            <p className="font-['Share_Tech_Mono'] text-[#333] text-xs tracking-[0.2em] uppercase mb-8">
              Tell the author. It matters more than you think.
            </p>
            <button
              onClick={() => navigate("/want-more")}
              className="inline-flex items-center gap-3 bg-[#C8922A] hover:bg-[#D9A030] text-black text-sm tracking-[0.2em] uppercase font-['Share_Tech_Mono'] font-bold px-10 py-4 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-fire-line text-base" />
              I Want More
            </button>
          </div>
        </div>
      </div>

      {/* Floating TOC Button */}
      {showFloat && (
        <button
          onClick={scrollToTOC}
          className="fixed bottom-8 right-8 z-40 flex items-center gap-2 bg-[#0A0A0A] border border-[#C8922A]/40 hover:border-[#C8922A] text-[#C8922A] font-['Share_Tech_Mono'] text-xs tracking-[0.2em] uppercase px-4 py-3 transition-all duration-300 cursor-pointer whitespace-nowrap"
          style={{ boxShadow: "0 0 30px rgba(0,0,0,0.8)" }}
        >
          <i className="ri-list-unordered text-sm" />
          Contents
        </button>
      )}

      <Footer />
    </div>
  );
}
